import { createContext, Dispatch, SetStateAction } from "react";
import { PROJECT_SETTINGS_DEFAULT } from "../common/constants/ProjectConst";
import { IFunctionPermissions } from "../common/api_interfaces/role/IFunctionPermissions";
import { ITag } from "../common/api_interfaces/tag/ITag";
import { IProjectHistoryDisplay } from "../common/api_interfaces/history/IProjectHistoryDisplay";
import { IProjectMinimal } from "../common/api_interfaces/project/ICardProjectProject";
import { ProjectSettings } from "../common/api_interfaces/project/ProjectSettings";
import { ITaskChecklistGroup } from "../common/api_interfaces/taskChecklist/ITaskChecklistGroup";
import { ITaskGeneralInfo } from "../common/api_interfaces/task/ITaskGeneralInfo";
import { ITaskHistoryFilter } from "../common/api_interfaces/history/ITaskHistoryFilter";
import { IGetAllTaskPerson, IPerson } from "../common/api_interfaces/task/IGetAllTaskPerson";
import { ISubTask } from "../common/api_interfaces/task/ISubTask";
import { ISubTaskView } from "../common/api_interfaces/task/ISubTaskView";
import { ITeamMemberDisplay } from "../common/api_interfaces/project/ITeamDisplay";

interface TaskContextType {
    team: ITeamMemberDisplay[] | undefined;

    // Состояния данных задачи
    taskInformation: ITaskGeneralInfo | undefined;
    allPerson: IGetAllTaskPerson | undefined;
    projectSettings: ProjectSettings;
    defaultBoardId: number | undefined;
    timeSelectVariant: number | undefined;

    // Состояния подзадач
    subTasks: ISubTask[];
    allowedProjects: IProjectMinimal[];
    subtasksHead: ISubTaskView | undefined;

    // Состояния чеклистов
    checklistGroups: ITaskChecklistGroup[];

    // Состояния истории
    taskHistory: IProjectHistoryDisplay[];
    taskHistoryFilters: ITaskHistoryFilter;
    historySkip: number;
    haveMoreRecords: boolean;
    viewHistory: boolean;

    // UI состояния
    tab: number | undefined;
    locationHash: string | undefined;
    editTask: boolean;
    isLoading: boolean;
    forceLinkTerm: boolean;
    forceLinkName: boolean;
    forceLinkDescription: boolean;
    forceLinkExecutor: boolean;

    // Права доступа
    userAccess: IFunctionPermissions[];

    // Референсы
    scrollComponentDiv: React.RefObject<HTMLDivElement> | null;

    // Вспомогательные значения
    currentPageBoardId: number | undefined; // Либо boardId из url либо из задачи
    defaultTab: string | null;
    checkList: number | null;

    // Функции загрузки данных
    loadTaskInfo: () => Promise<void | ITaskGeneralInfo>;
    getAllTaskPerson: () => Promise<void>;
    loadSubTasks: () => Promise<void>;
    loadChecklist: (curentAccess?: IFunctionPermissions[]) => Promise<void>;
    loadTaskHistory: (
        skip: number,
        take: number,
        filters: ITaskHistoryFilter | undefined,
        append: boolean
    ) => Promise<boolean>;

    // Функции обновления данных
    updatePriority: (id: number) => void;
    updateType: (id: number) => void;
    addTags: (_tags: ITag[]) => Promise<void>;
    removeTags: (_tags: ITag | ITag[]) => Promise<void>;
    onChangeCustomTime: (event: React.ChangeEvent<HTMLInputElement>) => void;
    selectTimeVariant: (event: React.MouseEvent<HTMLButtonElement>) => void;

    fastTaskUpdate: (
        percentageCompletion?: number,
        requiredTime?: number,
        dateStart?: Date,
        dateEnd?: Date,
        responsible?: number,
        group?: number[]
    ) => Promise<void>;

    // Функции UI взаимодействия
    changeTab: (newTab: number) => void;
    onScroll: (e: React.UIEvent<HTMLDivElement>) => Promise<void>;
    onSubTaskSelectControlClick?: (task: ISubTask) => void;

    // Сеттеры состояний
    setTaskInformation: React.Dispatch<React.SetStateAction<ITaskGeneralInfo | undefined>>;
    setAllPerson: React.Dispatch<React.SetStateAction<IGetAllTaskPerson | undefined>>;
    setSubTasks: React.Dispatch<React.SetStateAction<ISubTask[]>>;
    setAllowedProjects: React.Dispatch<React.SetStateAction<IProjectMinimal[]>>;
    setProjectSettings: React.Dispatch<React.SetStateAction<ProjectSettings>>;
    setCheklistGroups: React.Dispatch<React.SetStateAction<ITaskChecklistGroup[]>>;
    setTaskHistory: React.Dispatch<React.SetStateAction<IProjectHistoryDisplay[]>>;
    setTaskHistoryFilters: React.Dispatch<React.SetStateAction<ITaskHistoryFilter>>;
    setHistorySkip: React.Dispatch<React.SetStateAction<number>>;
    setHaveMoreRecords: React.Dispatch<React.SetStateAction<boolean>>;
    setTab: React.Dispatch<React.SetStateAction<number | undefined>>;
    setLocationHash: React.Dispatch<React.SetStateAction<string | undefined>>;
    setEditTask: React.Dispatch<React.SetStateAction<boolean>>;
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
    setUserAccess: React.Dispatch<React.SetStateAction<IFunctionPermissions[]>>;
    setViewHistory: React.Dispatch<React.SetStateAction<boolean>>;
    setDefaultBoardId: React.Dispatch<React.SetStateAction<number | undefined>>;
    setSubtasksHead: React.Dispatch<React.SetStateAction<ISubTaskView | undefined>>;
    setTimeSelectVariant: React.Dispatch<React.SetStateAction<number | undefined>>;

    // Быстрое обновление
    setPercentageCompletion: React.Dispatch<React.SetStateAction<number | undefined>>;
    setRequiredTime: React.Dispatch<React.SetStateAction<number | undefined>>;
    setDateStart: React.Dispatch<React.SetStateAction<Date | undefined>>;
    setDateEnd: React.Dispatch<React.SetStateAction<Date | undefined>>;
    setExecutor: React.Dispatch<React.SetStateAction<IPerson | undefined | null>>;
    setGroup: React.Dispatch<React.SetStateAction<IPerson[] | undefined>>;

    dateStart: Date | undefined;
    dateEnd: Date | undefined;
    percentageCompletion: number | undefined;
    requiredTime: number | undefined;
    executor: IPerson | undefined | null;
    group: IPerson[] | undefined;
    visibleGroup: IPerson[] | undefined;
    hiddenGroup: IPerson[] | undefined;
    hasMore: boolean;
    filteredChoiseExecutor: {
        id: number;
        role: string;
        name: string;
        surname: string;
        middleName: string;
        photoId: number | undefined;
    }[];
    filteredChoiceGroup: {
        id: number;
        role: string;
        name: string;
        surname: string;
        middleName: string;
        photoId: number | undefined;
    }[];

    handleSelectExecutor: (userId: number | null) => Promise<void>;
    handleSelectGroup: (userId: number) => Promise<void>;
    handleRemoveGroup: (userId: number) => Promise<void>;
    updatePercentageCompletion: (percentageCompletion: number) => Promise<void>;
    updateRequiredTime: (requiredTime: number) => Promise<void>;
    updateDate: (typeDate: "start" | "end", date: Date) => Promise<void>;
}

export const TaskContext = createContext<TaskContextType>({
    team: undefined,

    // Состояния данных задачи
    taskInformation: undefined,
    allPerson: undefined,
    projectSettings: PROJECT_SETTINGS_DEFAULT,
    defaultBoardId: undefined,
    timeSelectVariant: undefined,

    // Состояния подзадач
    subTasks: [],
    allowedProjects: [],
    subtasksHead: undefined,

    // Состояния чеклистов
    checklistGroups: [],

    // Состояния истории
    taskHistory: [],
    taskHistoryFilters: { taskId: 0 },
    historySkip: 0,
    haveMoreRecords: true,
    viewHistory: false,

    // UI состояния
    tab: undefined,
    locationHash: undefined,
    editTask: false,
    isLoading: true,
    forceLinkName: false,
    forceLinkDescription: false,
    forceLinkTerm: false,
    forceLinkExecutor: false,

    // Права доступа
    userAccess: [],

    // Референсы
    scrollComponentDiv: null,

    // Вспомогательные значения
    currentPageBoardId: undefined,
    defaultTab: null,
    checkList: null,

    // Функции загрузки данных
    loadTaskInfo: async () => {
        /* non */
    },
    getAllTaskPerson: async () => {
        /* non */
    },
    loadSubTasks: async () => {
        /* non */
    },
    loadChecklist: async () => {
        /* non */
    },
    loadTaskHistory: async () => false,

    fastTaskUpdate: async () => {},

    // Функции обновления данных
    updatePriority: async () => {
        /* non */
    },
    updateType: async () => {
        /* non */
    },
    addTags: async () => {
        /* non */
    },
    removeTags: async () => {
        /* non */
    },
    onChangeCustomTime: () => {},
    selectTimeVariant: () => {},

    // Функции UI взаимодействия
    changeTab: () => {
        /* non */
    },
    onScroll: async () => {
        /* non */
    },
    onSubTaskSelectControlClick: undefined,

    // Сеттеры состояний
    setTaskInformation: () => {
        /* non */
    },
    setAllPerson: () => {
        /* non */
    },
    setSubTasks: () => {
        /* non */
    },
    setAllowedProjects: () => {
        /* non */
    },
    setProjectSettings: () => {
        /* non */
    },
    setCheklistGroups: () => {
        /* non */
    },
    setTaskHistory: () => {
        /* non */
    },
    setTaskHistoryFilters: () => {
        /* non */
    },
    setHistorySkip: () => {
        /* non */
    },
    setHaveMoreRecords: () => {
        /* non */
    },
    setTab: () => {
        /* non */
    },
    setLocationHash: () => {
        /* non */
    },
    setEditTask: () => {
        /* non */
    },
    setIsLoading: () => {
        /* non */
    },
    setUserAccess: () => {
        /* non */
    },
    setViewHistory: () => {
        /* non */
    },
    setDefaultBoardId: () => {
        /* non */
    },
    setSubtasksHead: () => {
        /* non */
    },
    setTimeSelectVariant: () => {
        /* non */
    },

    // Быстрое обновление
    setPercentageCompletion: () => {
        /* non */
    },
    setRequiredTime: () => {
        /* non */
    },
    setDateStart: () => {
        /* non */
    },
    setDateEnd: () => {
        /* non */
    },
    setExecutor: () => {
        /* non */
    },
    setGroup: () => {
        /* non */
    },

    dateStart: undefined,
    dateEnd: undefined,
    percentageCompletion: undefined,
    requiredTime: undefined,
    executor: undefined,
    group: undefined,
    visibleGroup: undefined,
    hiddenGroup: undefined,
    hasMore: false,
    filteredChoiseExecutor: [],
    filteredChoiceGroup: [],

    handleSelectExecutor: async (userId: number | null) => {},
    handleSelectGroup: async (userId: number) => {},
    handleRemoveGroup: async (userId: number) => {},
    updatePercentageCompletion: async (percentageCompletion: number) => {
        /* non */
    },
    updateRequiredTime: async (requiredTime: number) => {
        /* non */
    },
    updateDate: async (typeDate: "start" | "end", date: Date) => {
        /* non */
    },
});
